import { useEffect, useRef, useState } from "react";
import { useCountdown } from "@/hooks/useCountdown";
import { Pencil, Trash2, RotateCcw } from "lucide-react";
import { boardKeyFromName } from "@/components/BoardTracker";
import { getGlowColor } from "@/lib/glowUtils";

const LONG_PRESS_MS = 1000;

function pad(n) {
  return n.toString().padStart(2, "0");
}

export default function TimerCard({ card, onEdit, onDelete, onReset, onClaim, onProxyClaim, onReadyTransition }) {
  const [isHovered, setIsHovered] = useState(false);
  const { days, hours, minutes, seconds, isReady } = useCountdown(card.target_at);
  const totalMinutes = days * 1440 + hours * 60 + minutes;
  const boardKey = boardKeyFromName(card.name);

  // Long-press tracking for touch devices
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  // Fire transition exactly once when ready flag flips
  useEffect(() => {
    if (isReady && !card.notified) {
      onReadyTransition?.(card);
    }
  }, [isReady, card, onReadyTransition]);

  const getCardColor = (cardName) => {
    if (cardName.includes('Red')) return 'text-red-500';
    if (cardName.includes('Blue')) return 'text-blue-400';
    if (cardName.includes('Board')) return 'text-orange-500';
    if (cardName.includes('Wasteland')) return 'text-blue-500';
    if (cardName.includes('Crypt')) return 'text-green-500';
    if (cardName.includes('Last Resort')) return 'text-yellow-500';
    return 'text-orange-500';
  };

  const cardColor = getCardColor(card.name);

  const stateClass = isReady
    ? "border-teal-600/60 animate-pulse-ready"
    : "border-orange-500/25 hover:border-orange-400/60";

  const glow = isReady
    ? "shadow-[0_0_20px_rgba(20,184,166,0.12)_inset]"
    : "shadow-[0_0_14px_rgba(249,115,22,0.08)_inset]";

  return (
    <div
      className={`group relative border-thin bg-[#121822] p-2 sm:p-3 transition-colors duration-300 corner-tl corner-tr corner-bl corner-br max-w-xs w-full ${isHovered ? 'bg-slate-800/20' : ''} ${stateClass} ${glow}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`timer-card-${card.id}`}
    >
      <div className="mb-1 px-2 py-1">
        <div className="flex items-start mb-1">
          <div className="flex-1 min-w-0">
            <div className="text-xs tracking-[0.3em] uppercase text-slate-300 truncate">
              {card.source || "Unspecified Source"}
            </div>
            <h3
              className={`font-bold leading-tight text-slate-200 truncate`}
              style={{ 
                fontSize: 'clamp(0.75rem, 3.5vw, 1.125rem)',
                textShadow: `0 0 5px ${getGlowColor(cardColor)}, 0 0 10px ${getGlowColor(cardColor)}, 0 0 20px ${getGlowColor(cardColor)}, 0 0 30px ${getGlowColor(cardColor)}`
              }}
              data-testid={`card-name-${card.id}`}
              title={card.name}
            >
              {card.name}
            </h3>
          </div>
        </div>
      </div>

      {/* Edit / reset buttons — hidden during claim state */}
      {!isReady && <div className="absolute top-1 right-1 flex flex-col items-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <div className="flex gap-1">
          <button
            onClick={() => onReset(card)}
            className="p-1.5 sm:p-2 border-thin border-slate-700 text-slate-400 hover:border-teal-600 hover:text-teal-400 transition-colors"
            data-testid={`reset-card-${card.id}`}
            aria-label="Reset timer to default"
            title="Reset timer (re-arm after claim)"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onEdit(card)}
            className="p-1.5 sm:p-2 border-thin border-slate-700 text-slate-400 hover:border-orange-500 hover:text-orange-400 transition-colors"
            data-testid={`edit-card-${card.id}`}
            aria-label="Edit card"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>
        <button
          onClick={() => onDelete(card)}
          className="p-1.5 sm:p-2 border-thin border-slate-700 text-slate-400 hover:border-orange-600 hover:text-orange-400 transition-colors"
          data-testid={`delete-card-${card.id}`}
          aria-label="Delete card"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>}

      {/* Delete button always visible — accessible even in claim/ready state */}
      {isReady && (
        <button
          onClick={() => onDelete(card)}
          className="absolute top-1 right-1 p-1.5 sm:p-2 border-thin border-slate-700 text-slate-500 hover:border-orange-600 hover:text-orange-400 transition-colors opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
          data-testid={`delete-card-ready-${card.id}`}
          aria-label="Delete card"
          title="Delete timer"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}

      <div className="flex items-center justify-center h-[120px]">
        {isReady ? (
          <button
            type="button"
            onClick={() => {
              // Ignore click if it was preceded by a long-press on touch
              if (longPressFired.current) {
                longPressFired.current = false;
                return;
              }
              onClaim?.(card, boardKey);
            }}
            onMouseDown={(e) => {
              // Middle mouse button (button === 1) → proxy claim
              if (e.button === 1) {
                e.preventDefault();
                onProxyClaim?.(card, boardKey);
              }
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              longPressFired.current = false;
              longPressTimer.current = setTimeout(() => {
                longPressFired.current = true;
                onProxyClaim?.(card, boardKey);
              }, LONG_PRESS_MS);
            }}
            onTouchEnd={() => {
              clearTimeout(longPressTimer.current);
            }}
            onTouchMove={() => {
              // Cancel long press if the user scrolls
              clearTimeout(longPressTimer.current);
            }}
            data-testid={`claim-card-${card.id}`}
            title="Click to claim · Middle-click or long-press to proxy claim"
            className="w-full h-full flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 border-thin border-teal-600/60 bg-teal-600/10 text-teal-300 hover:text-teal-200 transition-all text-sm tracking-[0.3em] uppercase font-bold shadow-[0_0_12px_rgba(20,184,166,0.4)] hover:shadow-[0_0_20px_rgba(20,184,166,0.8)]"
          >
            {boardKey ? `Claim Board ${boardKey.split("_")[1]}` : `Claim ${card.name}`}
          </button>
        ) : (
          <div className="w-full">
            <div className="flex items-center justify-center mb-1 h-3" />
            <div className="flex items-end justify-center gap-4 sm:gap-3 tabular-nums">
              <TimeBlock value={totalMinutes} label="Min" wide color={cardColor} />
              <span className="font-dseg7 text-orange-400/40 text-3xl sm:text-2xl leading-none pb-1">:</span>
              <TimeBlock value={seconds} label="Sec" color={cardColor} />
            </div>
          </div>
        )}
      </div>

      {card.notes && (
        <div className="mt-4 pt-3 border-t border-thin border-slate-800 text-sm text-slate-400 leading-relaxed line-clamp-2" data-testid={`card-notes-${card.id}`}>
          {card.notes}
        </div>
      )}
    </div>
  );
}

function TimeBlock({ value, label, wide, color = 'text-white' }) {
  const text = wide ? value.toString().padStart(2, "0") : pad(value);
  const glowColor = getGlowColor(color);
  return (
    <div className="flex flex-col items-center">
      <span className={`font-dseg7 text-6xl sm:text-5xl leading-none text-slate-300`} style={{ textShadow: `0 0 5px ${glowColor}, 0 0 10px ${glowColor}, 0 0 20px ${glowColor}, 0 0 30px ${glowColor}` }}>{text}</span>
      <span className="text-[11px] tracking-[0.3em] uppercase text-slate-500 mt-0.5">{label}</span>
    </div>
  );
}

